<template>
  <div
    class="DocumentElementImageFile relative text-center group"
    style="min-height: 200px"
    :class="{
      DocumentHasPlaceholder: parent.placeholder,
      DocumentHighlightSearch: $route.hash === '#el' + element.id
    }"
    :id="'el' + element.id"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <tippy 
      v-if="file?.deleted"
      :content="$t('File was deleted')" 
      to="parent"
    />
    <tippy 
      v-else-if="file_upload?.id"
      :content="$t('Waiting for download file from external storage')" 
      to="parent"
    />
    <tippy 
      v-else-if="antivirusState === 'hasVirus'"
      :content="$t('File has virus')" 
      theme='warning'
      to="parent"
    />
    <!-- Point to mark the image object -->
    <span class="iconsWrapper">
      <span
        v-if="!file?.deleted"
        class="mr-2.5 sm:mr-3.5 absolute right-8 top-1"
        :class="{'invisible': !isHovered}"
      >
        <aw-icon 
          v-if="antivirusState === 'waiting'"
          v-tippy="$t('File is being checked for viruses')"
          icon="safety-check" 
          class="text-gray-400 w-5 h-5"
        />
        <aw-icon 
          v-if="antivirusState === 'checked'"
          v-tippy="$t('File is safe')"
          icon="verified-user" 
          class="text-green-600 w-5 h-5"
        />
        <span>
          <aw-icon
            icon="info"
            class="w-5 h-5 current-fill cursor-pointer text-gray-400"
          />
          <document-element-file-metadata-tippy
            :antivirus-state="antivirusState"
            :element="element"
          />
        </span>
      </span>
      <span
        ref="DocumentCircle"
        class="DocumentCircle mr-2.5 sm:mr-3.5 absolute right-3 top-2.5"
        :class="{
          'DocumentCircle--active': bcSelectedStore.selected.has(element.id),
          'invisible': !bcSelectedStore.selected.has(element.id) && !isHovered,
          'DocumentCircle--inactive': parent.type != 'image',
        }"
        :data-item-index="element.root_rank"
        @click.prevent.stop="bcSelectedStore.select(
          element, 
          {
            isRecursive: true,
            isShift: $event.shiftKey,
            isDocument: true,
            itemIndex: element.root_rank,
          }
        )"
      />
    </span>
    <!-- //Point to mark the image object -->

    <span
      v-if="parent.type === 'preview' && parent.children.length > 1"
      @click="prevImage()"
      class="select-none w-12 h-12 bg-white bg-opacity-80 hover:bg-opacity-100 absolute left-0 top-1/2 flex items-center justify-center z-3 cursor-pointer transform -translate-y-1/2"
    >
      <aw-icon icon="chevron-left" class="w-8 h-8 text-base" />
    </span>

    <span
      v-if="parent.type === 'preview' && parent.children.length > 1"
      @click="nextImage()"
      class="select-none w-12 h-12 bg-white bg-opacity-80 hover:bg-opacity-100 absolute right-0 top-1/2 flex items-center justify-center z-3 cursor-pointer transform -translate-y-1/2"
    >
      <aw-icon icon="chevron-right" class="w-8 h-8 text-base" />
    </span>

    <div :class="{ ScaleImage: isVideo && canShowInGallery }">
      <div
        v-if="antivirusState === 'hasVirus'"
        class="flex items-center justify-center flex-col text-center absolute inset-0 pointer-events-none select-none text-red-600"
      >
        <aw-icon 
          icon="bug-report" 
          class="h-24 w-24"
        />
        <div class="text-xs text-center">
          {{ element.file.name }}
        </div>
      </div>

      <div
        v-else-if="file?.deleted"
        class="flex items-center justify-center flex-col text-center absolute inset-0 pointer-events-none select-none text-gray-300"
      >
        <aw-icon 
          icon="trash-slim"
          class="h-24 w-24"
        />
        <div class="text-xs text-center">
          {{ element.file.name }}
        </div>
      </div>

      <div
        v-else-if="antivirusState === 'waiting' || !canShowInGallery"
        class="flex items-center justify-center text-center absolute inset-0 pointer-events-none select-none"
      >
        <file-icon 
          :ext="fileName" 
          size="large" 
        />
      </div>
      
      <bc-video
        v-else-if="isVideo"
        :video-settings="videoSettings"
      />

      <bc-audio
        v-else-if="isAudio"
        :audio-settings="{
          src: element.file.previews['audio'],
          ext: element.file.ext
        }"
      />

      <animation-viewer
        v-else-if="element?.file?.type === 'animation'"
        :file="element.file"
      />

      <template
        v-else-if="hasPreview"
      >
        <div
          v-if="element?.file?.previews['1000x']"
          v-show="imagePreviewLoaded"
          :class="{'bg-checkered-lg' : blackBackground}"
        >
          <img
            :src="imagePreviewLoaded ? element.file.previews['1000x'] : undefined"
            @click="openImage()"
            alt=""
            class="cursor-pointer"
            ref="imagePreview"
          />
        </div>

        <div
          v-if="!imagePreviewLoaded"
          class="flex items-center justify-center absolute inset-0 pointer-events-none select-none"
        >
          <aw-icon icon="loader" class="h-24 w-24 text-bc-blue" />
        </div>
      </template>
    </div>

    <div
      v-if="
        element?.file?.previews &&
        element?.file?.previews['1000x'] &&
        element?.file?.id_canva_design &&
        !$presenter.isPublic
      "
      class="hidden items-center justify-center text-center absolute inset-0 select-none group-hover:flex"
    >
      <a class="flex-grow relative z-1" :href="canvaRemixUrl" target="_blank">
        <aw-icon icon="canva" class="h-24 w-24 text-bc-blue" />
        <div class="text-lg flex-grow text-center text-bc-blue">
          {{ $t('Use Canva template') }}
        </div>
      </a>
      <div class="absolute inset-0 bg-white opacity-80"></div>
    </div>
  </div>
</template>

<script>
import elementsBoxModel from '../../elementsBoxModel'
import FileState from '../../mixins/FileState'
import FileElement from '../../mixins/FileElement'
import ImagePreview from '../../mixins/ImagePreview'
import Selected from '../../mixins/Selected'

export default {
  mixins: [
    FileState,
    FileElement,
    ImagePreview,
    Selected,
  ],
  emits: ['on-prev-image', 'on-next-image', 'on-open-image'],
  data() {
    return {
      isHovered: false
    }
  },
  computed: {
    canvaRemixUrl() {
      return (
        'https://canva.com/design/' +
        this.element.file.id_canva_design +
        '/remix'
      )
    },
    minElOffsetTop() {
      return elementsBoxModel.header.height + elementsBoxModel.breadcrumbs.height + (elementsBoxModel['elementHeading' + this.page.id] ? elementsBoxModel['elementHeading' + this.page.id].height : 61)
    },
    videoSettings() {
      return {
        src: this.element.file.previews['video'],
        poster: this.element.file.previews['1000x'],
        resolution: this.element.file.resolution,
        minElOffsetTop: this.minElOffsetTop,
        canDownload: this.element.data.can_download && this.element.file.can_download
      }
    }
  },
  methods: {
    prevImage() {
      this.$emit('on-prev-image')
    },
    nextImage() {
      this.$emit('on-next-image')
    },
    openImage() {
      this.$emit('on-open-image')
    }
  },
  mounted() {
    this.assignItemToSelectBtn('DocumentCircle', this.element)
  },
  watch: {
    'element.file.previews': {
      async handler(val) {
        if (val && val['1000x']) {
          await this.doLoadImagePreview('1000x')
        } else {
          this.imagePreviewLoaded = false
        }
      },
      immediate: true,
    }
  }
}
</script>

<style lang="scss">
.DocumentElementImageFile:has(.ScaleImage) {
  .iconsWrapper {
    > * ,
    > * > * {
      z-index: 2;
    }

    > * {
      @apply mt-4;
    }
  }
}

.ScaleImage {
  // max-height: calc(100vw - 160px);
  // border: 2px solid green;

  .aw-video {
    @apply mx-8 pt-4;
  }
  img,
  .img {
    max-height: calc(100vh - 200px);
  }
}
</style>
